import React from 'react';
import { Alert, Box, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { TableChart as TableIcon } from '@mui/icons-material';
import type { AnalysisAnswer } from '@/store/api/searchApi';
import { zustandLabel } from '@/utils/status-labels';

/**
 * Die gerechnete Antwort: Kopf, Tabelle, Vermerk.
 *
 * Der Kopf nennt die gewählte Auswertung und ihre Parameter — das ist die
 * Stelle, an der ein Missverständnis auffällt. „Nachweis" heißt im Haus
 * zweierlei: der Beleg an unserer Feststellung und die Unterlage, die dem
 * geprüften Bereich fehlt. Im Fließtext verschwindet der Unterschied, im Kopf
 * nicht.
 *
 * Der Vermerk darunter trennt die beiden Antwortarten, die im selben Fenster
 * erscheinen: gerechnet und formuliert.
 */

const ZIEL: Record<string, (id: string) => string> = {
  finding: (id) => `/findings/${id}`,
  measure: (id) => `/measures/${id}`,
};

/** Ob die Auswertung eingeschränkt wurde — eine leere Tabelle heißt dann etwas anderes. */
const eingeschraenkt = (a: AnalysisAnswer): boolean =>
  Object.values(a.parameters ?? {}).some((w) => w !== undefined && w !== null && w !== '');

export const AnalysisTable: React.FC<{ a: AnalysisAnswer; onSprung: (pfad: string) => void }> = ({ a, onSprung }) => (
  <Box>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
      <TableIcon fontSize="small" sx={{ color: 'ki.main' }} />
      <Typography variant="subtitle2" fontWeight={600}>
        {a.name}
      </Typography>
    </Stack>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
      {a.parameterText} · {a.rowCount === 1 ? '1 Zeile' : `${a.rowCount} Zeilen`}
    </Typography>

    {a.rowCount === 0 ? (
      /*
       * Eine leere Tabelle heißt zweierlei, und der Unterschied ist groß.
       * Ohne Einschränkung ist sie eine gute Nachricht: nichts offen. Mit
       * Einschränkung sagt sie nur, dass zu *dieser* Einschränkung nichts
       * passt — und dann ist die nächste Frage, ob die Einschränkung stimmt.
       * Beides als Erfolg zu melden wäre die gefährlichere Hälfte.
       */
      eingeschraenkt(a) ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          Keine Zeile mit dieser Einschränkung. Im Kopf steht, welche gesetzt wurde — passt sie nicht,
          fragen Sie anders.
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 1 }}>
          Keine Zeile — zu dieser Auswertung gibt es nichts Offenes.
        </Alert>
      )
    ) : (
      <Paper variant="outlined" sx={{ overflowX: 'auto', mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {a.columns.map((s) => (
                // Die Überschriften dürfen umbrechen. Einzeilig erzwungen
                // haben sie die Tabelle breiter gemacht als das Fenster:
                // gemessen am 14.09.2026 bei den überfälligen Maßnahmen
                // 564 px Inhalt in 465 px Rahmen — „Zur Feststellung" allein
                // hielt 120 px besetzt, und die letzte Spalte stand halb am
                // Rand. Zweizeilig ist eine Überschrift noch lesbar; eine
                // abgeschnittene Spalte ist es nicht.
                <TableCell key={s.key} sx={{ fontWeight: 600, px: 1, lineHeight: 1.25 }}>
                  {s.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {a.rows.map((zeile, i) => {
              const id = String(zeile.id ?? '');
              const pfad = ZIEL[a.rowType]?.(id);
              return (
                <TableRow
                  key={id || i}
                  hover
                  onClick={pfad ? () => onSprung(pfad) : undefined}
                  sx={pfad ? { cursor: 'pointer' } : undefined}
                >
                  {a.columns.map((s, spalte) => (
                    <TableCell
                      key={s.key}
                      // Die kurzen Werte — Zustände und Daten — bleiben
                      // einzeilig, solange Platz dafür ist: im breiten
                      // Fenster. Wird es enger, brechen auch sie um. Ein Wort
                      // über zwei Zeilen liest sich schlechter als eines,
                      // eine abgeschnittene Spalte gar nicht.
                      sx={{
                        ...(spalte === 0 ? { minWidth: 110 } : {}),
                        ...(s.kind ? { whiteSpace: { xs: 'normal', lg: 'nowrap' } } : {}),
                        // Eine Kennung ohne Leerzeichen soll die Spalte nicht
                        // aufreißen, sondern umbrechen.
                        wordBreak: 'break-word',
                        px: 1,
                      }}
                    >
                      {s.kind === 'label'
                        ? zustandLabel(String(zeile[s.key] ?? ''))
                        : String(zeile[s.key] ?? '—')}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    )}

    <Typography variant="caption" color="text.secondary">
      {a.note}
    </Typography>
  </Box>
);

